/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useWallet } from '../providers/WalletProvider';
import { getJupiterQuote, JupiterQuoteResponse } from '../lib/solana/jupiter';
import { executeTradeApi } from '../api/trade';
import { jupiterApi } from '../api/jupiter';
import { SOL_MINT, connection } from '../lib/solana/solana';
import { fromBaseUnit, toBaseUnit } from '../lib/utils/format';
import { TokenMetadata } from '../lib/pinata';
import { bondingCurveDB } from '../lib/bonding-curve-db';
import { PLATFORM_TRADE_FEE_BPS } from '../lib/constants';
import { processTransaction } from '../lib/solana/processing';

export const useTrade = (
    token: TokenMetadata, 
    currentPrice: number, 
    initialSide: 'BUY' | 'SELL' = 'BUY',
    slippageOverride?: number
) => {
    const { isConnected, address, chain, balance, refreshBalance, network } = useWallet();
    
    // Inputs
    const [amountIn, setAmountIn] = useState<string>('');
    const [isBuy, setIsBuy] = useState(initialSide === 'BUY');
    
    // State
    const [tokenBalance, setTokenBalance] = useState<number>(0);
    const [quote, setQuote] = useState<JupiterQuoteResponse | null>(null);
    const [internalQuote, setInternalQuote] = useState<{ out: number, impact: number, fee: number, tax: number } | null>(null);
    
    // Status
    const [isLoading, setIsLoading] = useState(false);
    const [isSigning, setIsSigning] = useState(false); 
    const [isQuoting, setIsQuoting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [txHash, setTxHash] = useState<string | null>(null);
    
    // Slippage Management
    const [localSlippage, setLocalSlippage] = useState(0.5); 
    const activeSlippage = slippageOverride !== undefined ? slippageOverride : localSlippage;

    // Debounce Ref
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // 1. Reset side when prop changes
    useEffect(() => {
        setIsBuy(initialSide === 'BUY');
    }, [initialSide]);

    // 2. Fetch Token Balance (Real RPC)
    useEffect(() => {
        let mounted = true;
        
        const fetchTokenBalance = async () => {
            if (!isConnected || !address || !token.properties?.mintAddress) {
                if (mounted) setTokenBalance(0);
                return;
            }

            if (token.properties.chain === 'SOL') {
                try {
                    const accounts = await connection.getTokenAccountsByOwner(address, token.properties.mintAddress);
                    if (accounts.length > 0) {
                        const balanceInfo = await connection.getTokenAccountBalance(accounts[0]);
                        if (mounted && balanceInfo) setTokenBalance(balanceInfo.uiAmount);
                    } else {
                        if (mounted) setTokenBalance(0);
                    }
                } catch (e) {
                    // Silent fail for balance checks, assume 0
                    if (mounted) setTokenBalance(0);
                }
            } else {
                // SUI Mock
                const seed = address.length + token.symbol.length;
                const mockBal = (seed * 123.45) % 5000; 
                if (mounted) setTokenBalance(mockBal);
            }
        };

        fetchTokenBalance();
        const interval = setInterval(fetchTokenBalance, 15000); // Poll slower
        return () => { 
            mounted = false; 
            clearInterval(interval);
        };
    }, [isConnected, address, token]);

    // 3. Helper: Percentage Inputs
    const setAmountByPercentage = useCallback((percent: number) => {
        if (!isConnected) return;
        
        if (isBuy) {
            // Native SOL/SUI: Reserve 0.02 for gas
            const max = Math.max(0, balance - 0.02);
            const value = max * (percent / 100);
            setAmountIn(value > 0 ? value.toFixed(4) : '');
        } else {
            // Token
            const value = tokenBalance * (percent / 100);
            setAmountIn(value > 0 ? value.toFixed(token.properties?.decimals || 2) : '');
        }
    }, [isConnected, balance, tokenBalance, isBuy, token.properties?.decimals]);

    // 4. Quote Logic (Bonding Curve vs Jupiter)
    useEffect(() => {
        const fetchQuote = async () => {
            if (!amountIn || Number(amountIn) <= 0) {
                setQuote(null);
                setInternalQuote(null);
                setIsQuoting(false);
                return;
            }

            setIsQuoting(true);
            setError(null);

            try {
                const mintAddress = token.properties?.mintAddress;
                // Check internal DB state
                const curveState = mintAddress ? bondingCurveDB.getCurve(mintAddress) : null;

                // A. Internal Bonding Curve Quote
                if (curveState && !curveState.isGraduated) {
                    const k = curveState.virtualSolReserves * curveState.virtualTokenReserves;
                    let amountOut = 0;
                    let priceImpact = 0;
                    let fee = 0;
                    let tax = 0;
                    const amtInNum = Number(amountIn);
                    const transferFeeBps = token.properties?.extensions?.transferFeeBasisPoints || 0;

                    if (isBuy) {
                        fee = amtInNum * (PLATFORM_TRADE_FEE_BPS / 10000);
                        const netIn = amtInNum - fee;
                        const newSol = curveState.virtualSolReserves + netIn;
                        const newTokens = k / newSol;
                        let rawTokensOut = curveState.virtualTokenReserves - newTokens;
                        
                        if (transferFeeBps > 0) {
                            tax = rawTokensOut * (transferFeeBps / 10000);
                            amountOut = rawTokensOut - tax;
                        } else {
                            amountOut = rawTokensOut;
                        }
                        
                        const oldPrice = curveState.virtualSolReserves / curveState.virtualTokenReserves;
                        const newPrice = newSol / newTokens;
                        priceImpact = Math.abs((newPrice - oldPrice) / oldPrice) * 100;
                    } else {
                        // Sell
                        let netTokensIn = amtInNum;
                        if (transferFeeBps > 0) {
                            tax = amtInNum * (transferFeeBps / 10000);
                            netTokensIn = amtInNum - tax;
                        }
                        const newTokens = curveState.virtualTokenReserves + netTokensIn;
                        const newSol = k / newTokens;
                        const rawSolOut = curveState.virtualSolReserves - newSol;
                        fee = rawSolOut * (PLATFORM_TRADE_FEE_BPS / 10000);
                        amountOut = rawSolOut - fee;

                        const oldPrice = curveState.virtualSolReserves / curveState.virtualTokenReserves;
                        const newPrice = newSol / newTokens;
                        priceImpact = Math.abs((oldPrice - newPrice) / oldPrice) * 100;
                    }
                    
                    // Artificial Delay for realism
                    await new Promise(r => setTimeout(r, 300));
                    
                    setInternalQuote({ out: amountOut, impact: priceImpact, fee, tax });
                    setQuote(null);
                } 
                // B. External Aggregator Quote (Jupiter)
                else {
                    const inputMint = isBuy ? SOL_MINT : (token.properties?.mintAddress || SOL_MINT);
                    const outputMint = isBuy ? (token.properties?.mintAddress || SOL_MINT) : SOL_MINT;
                    const decimals = isBuy ? 9 : (token.properties?.decimals || 9);
                    const atomicAmount = Number(toBaseUnit(Number(amountIn), decimals));

                    const quoteData = await getJupiterQuote({
                        inputMint, 
                        outputMint, 
                        amount: atomicAmount, 
                        slippageBps: activeSlippage * 100
                    });
                    
                    setQuote(quoteData);
                    setInternalQuote(null);
                }
            } catch (e) {
                console.error("Quote failed", e);
                setQuote(null);
                setInternalQuote(null);
                setError("Failed to fetch quote.");
            } finally {
                setIsQuoting(false);
            }
        };

        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(fetchQuote, 500);
        
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        };
    }, [amountIn, isBuy, activeSlippage, token, chain]);

    // 5. Execute Logic
    const executeTrade = async () => {
        if (!isConnected || !address) {
            setError("Wallet not connected");
            return;
        }
        
        // Sanity Checks
        const numAmount = Number(amountIn);
        if (numAmount <= 0) {
            setError("Enter a valid amount");
            return;
        }
        if (isBuy && numAmount + 0.005 > balance) { // 0.005 buffer for rent/gas
            setError(`Insufficient ${chain} balance for trade + gas.`);
            return;
        }
        if (!isBuy && numAmount > tokenBalance) {
            setError(`Insufficient ${token.symbol} balance.`);
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            // A. Simulate Wallet Signature
            setIsSigning(true);
            await new Promise((resolve) => setTimeout(resolve, 1000));
            setIsSigning(false);

            let tradeResult;

            // B. Execute based on Route
            if (internalQuote) {
                // Internal Bonding Curve
                const result = await executeTradeApi({
                    tokenAddress: token.properties?.mintAddress || 'unknown',
                    tokenSymbol: token.symbol,
                    tokenSupply: token.properties?.supply || 1_000_000_000,
                    amountIn: numAmount,
                    side: isBuy ? 'BUY' : 'SELL',
                    slippageBps: activeSlippage * 100,
                    walletAddress: address,
                    chain: chain || 'SOL',
                    network: network || 'devnet',
                    transferFeeBasisPoints: token.properties?.extensions?.transferFeeBasisPoints
                }, currentPrice);

                if (!result.success || !result.data) throw new Error(result.error || "Trade failed");
                tradeResult = result.data;

            } else if (quote) {
                // External Aggregator
                const { swapTransaction } = await jupiterApi.swap(quote, address);
                const { signature, error: txError } = await processTransaction(swapTransaction);
                if (txError) throw new Error(txError);

                // Construct Result for UI
                tradeResult = {
                    txHash: signature,
                    amountOut: Number(estimatedOut),
                    fee: fromBaseUnit(quote.platformFee?.amount || '0', 9),
                    tokenTax: 0,
                    priceImpact: quote.priceImpactPct,
                    newPrice: currentPrice, // Jupiter doesn't return new spot price directly
                    newMarketCap: 0,
                    timestamp: Date.now(),
                    status: 'success',
                    blockNumber: 0,
                };
            } else {
                throw new Error("Quote expired. Please refresh.");
            }

            // C. Success Handling
            if (tradeResult) {
                setTxHash(tradeResult.txHash);
                refreshBalance();
                // Optimistic UI Update
                setTokenBalance(prev => isBuy ? prev + Number(estimatedOut) : prev - numAmount);
            }

        } catch (e: any) {
            setError(e.message || "Transaction failed");
        } finally {
            setIsLoading(false);
            setIsSigning(false);
        }
    };

    // Derived Display Values
    let estimatedOut = '0.00';
    let minReceived = '0.00';
    let priceImpact = '< 0.1';
    let rate = '-';

    if (internalQuote) {
        estimatedOut = internalQuote.out.toFixed(isBuy ? 2 : 4);
        const minRecVal = internalQuote.out * (1 - activeSlippage / 100);
        minReceived = minRecVal.toFixed(isBuy ? 2 : 4);
        priceImpact = internalQuote.impact.toFixed(2);
        if (Number(amountIn) > 0 && internalQuote.out > 0) {
            rate = (internalQuote.out / Number(amountIn)).toFixed(isBuy ? 2 : 6);
        }
    } else if (quote) {
        const outDecimals = isBuy ? (token.properties?.decimals || 9) : 9;
        const estimatedOutVal = fromBaseUnit(quote.outAmount, outDecimals);
        estimatedOut = estimatedOutVal > 0 ? estimatedOutVal.toFixed(isBuy ? 2 : 4) : '0.00';
        const minReceivedVal = estimatedOutVal * (1 - activeSlippage / 100);
        minReceived = minReceivedVal > 0 ? minReceivedVal.toFixed(isBuy ? 2 : 4) : '0.00';
        priceImpact = quote.priceImpactPct.toFixed(2);
        if (Number(amountIn) > 0 && estimatedOutVal > 0) {
            rate = (estimatedOutVal / Number(amountIn)).toFixed(isBuy ? 2 : 6);
        }
    }

    return {
        amountIn, setAmountIn, setAmountByPercentage,
        isBuy, setIsBuy,
        tokenBalance,
        estimatedOut, minReceived, priceImpact, rate,
        isLoading, isSigning, isQuoting,
        error, txHash, executeTrade,
        slippage: activeSlippage, setSlippage: setLocalSlippage,
        quote,
        internalQuote,
        reset: () => { setTxHash(null); setAmountIn(''); setError(null); }
    };
};